// src/App.tsx
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import type { ReactNode } from "react";
import { useEffect, useState } from "react";

import HomeScreen from "./screens/HomeScreen";
import CourseScreen from "./screens/energy-reset/CourseScreen";
import SetTimezoneScreen from "./screens/onboarding/SetTimezoneScreen";
import PwaInstallStep from "./screens/onboarding/PwaInstallStep";
import DashboardScreen from "./screens/energy-reset/DashboardScreen";
import OtpAuthScreen from "./screens/onboarding/OtpAuthScreen";
import UnauthScreen from "./screens/UnauthScreen";

import SupportWidget from "./components/SupportWidget";
import PrivacyPolicy from "./law_pages/PrivacyPolicy";
import OfferER14 from "./law_pages/energy-reset/OfferER14";

import TopBar from "./components/layout/TopBar";
import SparksWallet from "./components/wallet/SparksWallet";

import { APP_CONFIG, setDynamicConfig, applyDesign, setAuthFailureCallback } from "./utils/api";
import { auth } from "./api/auth";
import { getSnapshot, findSparks } from "./api/snapshot";
import { runPreload } from "./utils/preload";
import { flushQueue } from "./utils/queue";

function RequireAuth({ children }: { children: ReactNode }) {
  if (!auth.isAuthenticated()) return <Navigate to="/unauth" replace />;
  return <>{children}</>;
}

function App() {
  const [walletOpen, setWalletOpen] = useState(false);
  const [globalBalance, setGlobalBalance] = useState<number | null>(null);
  const [configLoaded, setConfigLoaded] = useState(false);
  // M5-STORE2 (16.09.2026): отдельно от configLoaded — "конфиг успешно
  // спросили" (пусть даже клиента у домена нет) vs "спросить не удалось"
  // (офлайн: /api/config не в SW runtime-кэше, vite.config.ts). Раньше оба
  // случая давали одинаковый saasClientId=null, и loadBalance ниже не мог их
  // различить — см. причину нулевых искорок в комментарии внутри loadBalance.
  const [configFetchFailed, setConfigFetchFailed] = useState(false);
  const [isAuthed, setIsAuthed] = useState(auth.isAuthenticated());

  // 1. ЗАГРУЗКА КОНФИГА ПРИ СТАРТЕ
  useEffect(() => {
    async function initApp() {
      try {
        const response = await fetch("/api/config");
        if (response.ok) {
          const config = await response.json();
          setDynamicConfig(config);
          applyDesign(config.design);
          console.log("Config loaded for:", window.location.host, config);
        } else {
          console.log("Using default config (main domain)");
        }
      } catch (e) {
        console.error("Config fetch failed, using defaults", e);
        setConfigFetchFailed(true);
      }

      await auth.init();
      setIsAuthed(auth.isAuthenticated());
      setAuthFailureCallback(() => {
        // При невозможности обновить токен — отправить на экран входа
        window.location.replace("/unauth");
      });
      setConfigLoaded(true);
    }
    initApp();
  }, []);

  // 2. ЗАГРУЗКА БАЛАНСА
  useEffect(() => {
    if (!configLoaded) return;

    let alive = true;
    async function loadBalance() {
      const saasClientId = APP_CONFIG.saasClientId;

      // M5-STORE2 (16.09.2026): установлено фактом — баланс показывал 0,
      // хотя в кэше лежало 60, НЕ из-за того, что ветка "cached" не
      // обрабатывалась наравне с "ok" (это уже было верно с M5-STORE1, см.
      // ветку ниже). Причина в этой ранней проверке: при офлайн-перезагрузке
      // /api/config не отвечает (нет SW runtime-кэша под /api/*, сверено по
      // pwa/vite.config.ts), APP_CONFIG.saasClientId остаётся дефолтным null,
      // и старый код читал "клиента нет" вместо "не смогли спросить" —
      // выходил здесь с 0 ДО того, как вообще пытался спросить снапшот/кэш.
      // Легитимный случай "у домена нет клиента" (configFetchFailed=false,
      // конфиг ответил) сохраняет старое поведение без изменений.
      if (!saasClientId && !configFetchFailed) { setGlobalBalance(0); return; }

      if (!auth.isAuthenticated()) return;
      const result = await getSnapshot();
      if (!alive) return;

      // SW-PRELOAD (19.09.2026), Шаг 4: только "ok" — живой снапшот, не кэш
      // (из кэша предзагружать бессмысленно, задание). Не ждём завершения —
      // runPreload сам идёт фоном и сам себя не запускает дважды за сессию.
      if (result.status === "ok") {
        void runPreload(result.data);
      }

      if (result.status === "ok" || result.status === "cached") {
        // M5-QUEUE (20.09.2026): запуск доставки отложенной очереди — "после
        // получения сессии" (задание). user_id есть и у живого, и у
        // кэшированного снапшота (оба означают "мы знаем, кто это", отличие
        // только в свежести данных, не в личности) — используем оба как
        // сигнал "сессия известна". Не await — доставка идёт фоном, не
        // должна задерживать отрисовку баланса.
        void flushQueue(result.data.user_id);

        // M5-STORE1: "cached" — офлайн, последний известный снимок; искорки
        // из него так же законны, как из живого ответа (индикатор не делаем).
        // Сервер уже скопил sparks на клиента текущей сессии (api/index.js,
        // /api/access/snapshot — массив содержит не больше одной записи), так
        // что при неизвестном saasClientId (офлайн-ветка выше) берём
        // единственную запись напрямую, не гадая чужой ключ; при известном —
        // findSparks даёт тот же результат, что и раньше (успешный путь не меняется).
        const s = saasClientId ? findSparks(result.data, saasClientId) : (result.data.sparks[0] ?? null);
        setGlobalBalance(s?.balance ?? 0);
      } else if (result.status === "empty") {
        setGlobalBalance(0);
      }
      // "unavailable" — не подставляем 0: оставляем прежнее значение
      // (globalBalance не трогаем; TopBar покажет "--", если значения ещё не было)
    }

    loadBalance();

    const onEarn = (e: Event) => {
      const anyEvt = e as CustomEvent;
      const amt = Number(anyEvt?.detail?.amount ?? 0);
      if (Number.isFinite(amt) && amt > 0) {
        setGlobalBalance((prev) => (prev ?? 0) + amt);
      }
    };

    window.addEventListener("wallet:earned", onEarn as EventListener);
    return () => {
      alive = false;
      window.removeEventListener("wallet:earned", onEarn as EventListener);
    };
    // PRELOAD-FIX (19.09.2026): находка трека E2E-OFFLINE — эффект зависел
    // только от configLoaded и срабатывал ровно один раз за загрузку
    // страницы, поэтому вход по коду внутри уже смонтированного приложения
    // не перезапускал его, и runPreload не вызывался вовсе до следующего
    // открытия PWA. isAuthed добавлен, чтобы эффект перезапускался и на
    // входе (и на выходе — ранняя проверка auth.isAuthenticated() на
    // выходе просто вернёт раньше времени, без побочных эффектов).
  }, [configLoaded, isAuthed]);

  // Реактивный статус авторизации — синхронизируется с saveToken/logout через auth:changed
  useEffect(() => {
    const onAuthChanged = () => setIsAuthed(auth.isAuthenticated());
    window.addEventListener("auth:changed", onAuthChanged);
    return () => window.removeEventListener("auth:changed", onAuthChanged);
  }, []);

  // QUEUE-UI-A (21.09.2026): queue:synced (utils/queue.ts) — доставка записи
  // очереди подтверждена свежим снапшотом в кэше. Баланс ПРИСВАИВАЕТСЯ из
  // этого снапшота, не прибавляется — сумма искорок за доставленный день
  // войдёт в него на сервере уже целиком; прибавление здесь задвоило бы
  // искорки при появлении начислений в части «б» этого трека.
  useEffect(() => {
    const onQueueSynced = () => {
      void (async () => {
        const saasClientId = APP_CONFIG.saasClientId;
        const result = await getSnapshot();
        if (result.status !== "ok" && result.status !== "cached") return;
        const s = saasClientId ? findSparks(result.data, saasClientId) : (result.data.sparks[0] ?? null);
        setGlobalBalance(s?.balance ?? 0);
      })();
    };
    window.addEventListener("queue:synced", onQueueSynced);
    return () => window.removeEventListener("queue:synced", onQueueSynced);
  }, []);

  if (!configLoaded) {
    return <div style={{ background: "#000", height: "100vh" }} />;
  }

  return (
    <BrowserRouter>
      <TopBar xp={globalBalance} onOpenWallet={() => setWalletOpen(true)} isAuthenticated={isAuthed} />

      <SparksWallet
        open={walletOpen}
        onClose={() => setWalletOpen(false)}
        balance={globalBalance ?? 0}
        nextReward={null}
      />

      <div style={{ paddingTop: "calc(env(safe-area-inset-top) + 56px)" }}>
        <Routes>
          <Route path="/" element={<HomeScreen />} />
          <Route path="/courses/:id" element={<CourseScreen />} />
          <Route path="/set-timezone" element={<SetTimezoneScreen />} />
          <Route path="/auth/otp" element={<OtpAuthScreen />} />
          <Route path="/unauth" element={<UnauthScreen />} />
          <Route
            path="/dashboard/:id"
            element={
              <RequireAuth>
                <DashboardScreen />
              </RequireAuth>
            }
          />
          <Route
            path="/onboarding/install-pwa"
            element={
              <RequireAuth>
                <PwaInstallStep nextRoute="/" />
              </RequireAuth>
            }
          />
          <Route
            path="/support"
            element={
              <RequireAuth>
                <SupportWidget source="dashboard" />
              </RequireAuth>
            }
          />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/oferta-er14" element={<OfferER14 />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </div>
    </BrowserRouter>
  );
}

export default App;
